import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MarketingNav from "@/components/site/MarketingNav";
import Footer from "@/components/site/Footer";
import { api } from "@/lib/api";

function renderMarkdown(md) {
  // Extremely lightweight markdown renderer — supports H2 and paragraphs, plus bullet lists.
  const blocks = md.split(/\n\n+/);
  return blocks.map((b, i) => {
    if (b.startsWith("## ")) {
      return (
        <h2 key={i} className="font-serif-display text-3xl md:text-4xl mt-12 leading-tight">
          {b.replace(/^##\s+/, "")}
        </h2>
      );
    }
    if (b.startsWith("- ")) {
      const items = b.split("\n").filter((l) => l.startsWith("- "));
      return (
        <ul key={i} className="mt-6 space-y-2 list-disc pl-6">
          {items.map((it, idx) => (
            <li key={idx}>{it.replace(/^-\s+/, "")}</li>
          ))}
        </ul>
      );
    }
    return (
      <p key={i} className="mt-6 leading-relaxed text-base md:text-lg text-foreground/90">
        {b}
      </p>
    );
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <MarketingNav />
        <div className="max-w-[1400px] mx-auto p-16">
          <div className="font-serif-display text-4xl">Not found</div>
          <Link to="/blog" className="mt-6 inline-block overline text-primary">← Back to Journal</Link>
        </div>
        <Footer />
      </div>
    );
  }
  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <MarketingNav />
        <div className="max-w-[1400px] mx-auto p-16 overline text-muted-foreground">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="blog-post-page">
      <MarketingNav />
      <article className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-20">
        <Link to="/blog" className="overline text-primary hover-invert" data-testid="back-to-journal">
          ← The Journal
        </Link>
        <div className="overline text-muted-foreground mt-8">
          {new Date(post.published_at).toLocaleDateString()} · {post.tags?.join(" · ")}
        </div>
        <h1 className="font-serif-display text-4xl md:text-6xl leading-none tracking-tight mt-4">
          {post.title}
        </h1>
        <p className="mt-6 text-lg text-muted-foreground italic">{post.excerpt}</p>
        <div className="rule-h my-10" />
        <div className="prose max-w-none">{renderMarkdown(post.body)}</div>
      </article>
      <Footer />
    </div>
  );
}
